// The "host" is the capability surface a plugin sees — the ONLY way plugin code
// (which lives outside src/) reaches into the runtime. Capabilities are grouped by
// concern so a contributor reads one doc block and picks what they need, instead of
// a flat grab-bag. See docs/PLUGINS.md for the authored contract.
//
// Built once per plugin at load (pluginLoader.loadOne); never per-frame. Per-frame
// pin reads are a separate, ctx-scoped object passed to tick() — see GraphEvaluator
// `pinReaderFor` — because those must close over the current frame's graph context.

import { ortBridge } from './ortBridge'
import { getCurrentArkit } from '../network/AudioAnimationBuffer'

// host.log is callable (host.log('x')) AND namespaced (host.log.warn/error). Keeping
// it callable preserves the bundled plugins' `host.log(...)` call sites.
function makeLog(pluginName) {
  const prefix = `[plugin.${pluginName}]`
  const log = (...args) => console.log(prefix, ...args)
  log.warn = (...args) => console.warn(prefix, ...args)
  log.error = (...args) => console.error(prefix, ...args)
  return log
}

/**
 * Build the capability object handed to a plugin's init()/tick().
 *
 * @param {string} pluginName  folder name (also the public asset base)
 * @param {object} manifest    the validated plugin.json (exposed as host.manifest)
 */
export function buildHost(pluginName, manifest) {
  const publicBase = `/plugins/${pluginName}`
  const host = {
    name: pluginName,
    manifest,
    // Declared for introspection/tests; mirrors the groups below.
    capabilities: ['log', 'assets', 'onnx', 'arkit'],

    log: makeLog(pluginName),

    assets: {
      // Resolve a runtime asset URL under this plugin's public folder
      // (public/plugins/<name>/<filename>).
      url: (filename) => `${publicBase}/${filename}`,
    },

    // ONNX Runtime Web sessions (see ortBridge.js). load() once in init(), keep the
    // returned id, run() per inference. run() is async — never block the frame; guard
    // with an `inflight` flag and let the result land a microtask later.
    onnx: {
      load: (url, opts) => ortBridge.load(url, opts),
      run: (sessionId, input) => ortBridge.run(sessionId, input),
      release: (sessionId) => ortBridge.release(sessionId),
    },

    // Live 52-element ARKit blendshape frame from the A2F audio buffer (the same
    // source LiveLink's __livelink__ path consumes). Returns a Float32Array(52)
    // reused per frame, or null when no Convorcher response is currently playing.
    arkit: {
      current: () => getCurrentArkit(),
    },
  }
  installLegacyAliases(host)
  return host
}

// Back-compat shim. The bundled ogs-* plugins call the original flat host
// methods; re-bind them onto the grouped host so those plugins stay
// byte-unchanged. New plugins (and plugins/_template) use the grouped form.
// Delete this once the bundled plugins migrate — same short-lived discipline
// CLAUDE.md applies to feature flags.
// @deprecated use the grouped capabilities (host.assets.url, host.onnx.load, …).
function installLegacyAliases(host) {
  host.assetUrl = host.assets.url
  host.getArkit = host.arkit.current
  host.ortLoad = host.onnx.load
  host.ortRun = host.onnx.run
  // host.log is already callable — no alias needed.
}

// Test/debug hook: build a host to assert the grouped capabilities + back-compat
// aliases (used by tests/e2e/ci/plugin-system.spec.js).
if (typeof window !== 'undefined') {
  window.__BUILD_HOST__ = buildHost
}
